import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import numbro from 'numbro';

import { BREAD_PRICES } from '../../../../constants/currencies';
import { currencyNames } from '../../../../constants/exchangeRates';
import { SVGIconType } from '../../../../constants/svgIcon';
import { AppBar, PngIcon, SvgIcon } from '../../../Molecules';

import styles from './GetMoreBreadTemplate.module.css';

type GetMoreBreadTemplateProps = {
  currency?: 'USD' | 'HKD' | 'CNY' | 'TWD';
  onClickPurchase: (amount?: number) => void;
};

const BreadRow = memo(
  ({
    amount,
    currency,
    onClick,
  }: {
    amount?: number;
    currency: 'USD' | 'HKD' | 'CNY' | 'TWD';
    onClick: (amount?: number) => void;
  }) => {
    const { t } = useTranslation();

    let price = t('Custom');
    if (amount === 1) {
      price = t('FREE');
    } else if (amount !== undefined) {
      price =
        currencyNames[currency] +
        numbro((BREAD_PRICES[currency] * amount) / 100 / 100).format({
          thousandSeparated: true,
        });
    }

    return (
      <div
        className={`${styles.breadRow} ${
          amount === 1 ? '' : styles.clickableRow
        }`}
        onClick={
          amount === 1
            ? undefined
            : () => {
                onClick(amount);
              }
        }
      >
        <div className={styles.icon}>
          <PngIcon name="Bread" width={40} height={40} />
          <div className={styles.subscript}>{`×${
            amount === undefined
              ? '?'
              : numbro(amount).format({ thousandSeparated: true })
          }`}</div>
        </div>
        <div className={styles.rowContent}>
          <div>
            {amount === undefined
              ? t('Custom')
              : `${numbro(amount).format({ thousandSeparated: true })} ${t(
                  'Bread',
                )}`}
          </div>
          {amount === 1 && (
            <div className={styles.rowDescription}>
              {t('Earn Bread by playing Memory Practices')}
            </div>
          )}
        </div>
        <div className={amount === 1 ? styles.freePrice : styles.price}>
          {price}
        </div>
      </div>
    );
  },
);

export const GetMoreBreadTemplate = ({
  currency = 'USD',
  onClickPurchase,
}: GetMoreBreadTemplateProps) => {
  const { t } = useTranslation();

  return (
    <div className={styles.container}>
      <AppBar />
      <div className={styles.card}>
        <SvgIcon
          name={SVGIconType.BreadDonate}
          width={140}
          height={140}
          color="#333333"
        />
        <div className={styles.title}>{t('Get More Bread')}</div>
        <div className={styles.subtitle}>
          {t('Help provide more bread to those in need.')}
        </div>
        <div className={styles.list}>
          <BreadRow amount={1} currency={currency} onClick={onClickPurchase} />
          <BreadRow
            amount={100}
            currency={currency}
            onClick={onClickPurchase}
          />
          <BreadRow
            amount={1000}
            currency={currency}
            onClick={onClickPurchase}
          />
          <BreadRow
            amount={10000}
            currency={currency}
            onClick={onClickPurchase}
          />
          <BreadRow
            amount={100000}
            currency={currency}
            onClick={onClickPurchase}
          />
          <BreadRow
            amount={undefined}
            currency={currency}
            onClick={onClickPurchase}
          />
        </div>
      </div>
    </div>
  );
};
